import React, { useEffect, useState, useContext, useCallback } from 'react';
import { Grid } from '@mui/material';
import Loading from "../../components/Loading/Loading";
import { getCombosPaginate } from "../../services/CombosServices";
import { CategoryFilter } from '../../components/CategoryFilter/CategoryFilter';
import { ProductList } from '../../components/ProductList/ProductList';
import ControlsRow from '../../components/ControlsRows/ControlsRows';
import { pdf } from '@react-pdf/renderer';
import ExportPdf from '../../components/pdf/exportPdf';
import { AuthContext } from "../../context/authContext";
import logoDefault from "../../images/productDefault.jpg";
import { useFilterParams } from '../../hooks/useFilterParams';

const getOrder = (ob) => {
  switch (ob) {
    case "precio_asc": return { orderBy: 'precioMostrar', orden: 'asc' };
    case "precio_desc": return { orderBy: 'precioMostrar', orden: 'desc' };
    case "nombre_asc": return { orderBy: 'art_descripcion', orden: 'asc' };
    case "nombre_desc": return { orderBy: 'art_descripcion', orden: 'desc' };
    case "codigo_asc": return { orderBy: 'art_codigo', orden: 'asc' };
    case "codigo_desc": return { orderBy: 'art_codigo', orden: 'desc' };
    default: return { orderBy: '', orden: '' };
  }
};

const CombosResults = () => {
  const { empresa, user } = useContext(AuthContext);

  const {
  searchTerm,
  selectedRamoId, selectedRubroId, selectedSubrubroId, selectedMarcaId,
  currentPage, perPage, orderBy,
  setRamoId, setRubroId, setSubrubroId, setMarcaId,
  setSearch, setCurrentPage, setPerPage, setOrderBy,
  clearAll,
} = useFilterParams();

  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [generandoPDF, setGenerandoPDF] = useState(false);
  const [viewMode, setViewMode] = useState('horizontal');

  useEffect(() => {
    setViewMode(empresa?.vista === false ? 'horizontal' : 'grid');
  }, [empresa]);

  const getImageSrc = useCallback((producto) => {
    const a = producto?.art_imagen?.trim();
    const e = empresa?.imagen_articulo?.trim();
    if (a) return `${process.env.REACT_APP_URL_BACKEND}${a}`;
    if (e) return `${process.env.REACT_APP_URL_BACKEND}${e}`;
    return logoDefault;
  }, [empresa]);

  const mapProducto = useCallback((producto) => {
    const stockFormatted = producto?.stock != null
      ? Number.isInteger(producto.stock)
        ? producto.stock.toString()
        : producto.stock.toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0, useGrouping: true })
      : "0";

    let textoPrecio;
    switch (producto?.tipoPrecioFinal) {
      case 2: textoPrecio = "Oferta"; break;
      case 1: textoPrecio = "Lista"; break;
      default: textoPrecio = "Disponible"; break;
    }

    const installment = producto?.stock > 0 ? `${textoPrecio}: ${stockFormatted} u` : "Sin Stock";

    const fractional= producto?.art_fraccionable === 1 ? true :false;

    const price = producto?.precioMostrar != null
      ? "$" + producto.precioMostrar.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : "Precio no disponible";

    const priceOld = producto?.precioVigente != null
      ? "$" + producto.precioVigente.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : null;

    const percentage = producto?.precioVigente > 0 && producto?.precioMostrar != null
      ? (((producto.precioVigente - producto.precioMostrar) / producto.precioVigente) * 100)
        .toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
      : null;

    return {
      id: producto.id, code: producto.art_codigo,
      category: producto?.Ramo?.descripcion_ramo ?? "Sin categoría",
      description: producto.art_descripcion, section: "TrendingProducts",
      imgSrc: getImageSrc(producto), altText: producto.art_descripcion,
      title: producto.art_descripcion ?? "Sin título",
      price, priceOld, percentage, stockActual: stockFormatted, installment,
      fractional,
      availability: producto?.art_desclarga ?? "", rating: null, reviews: 580,
    };
  }, [empresa, getImageSrc]);

  const fetchCombos = useCallback(async () => {
    setLoading(true);
    try {
      const cliente = JSON.parse(localStorage.getItem('clienteSelected'));
      const ord = getOrder(orderBy);

      const res = await getCombosPaginate({
        searchTerm,
        selectedRamo: selectedRamoId,      // ← ID para el backend
        selectedRubro: selectedRubroId,
        selectedSubrubro: selectedSubrubroId,
        selectedMarca: selectedMarcaId,
        page: currentPage, 
        perPage,
        user_id: cliente?.id,
        orderBy: ord.orderBy, orden: ord.orden,
      });

      if (res) {
        setProducts(res.combos.map(mapProducto));
        setTotalPages(res.pagination.totalPage);
        setTotalItems(res.pagination.totalItems);
      }
    } catch (e) {
      console.error("Error al obtener combos:", e);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedRamoId, selectedRubroId, selectedSubrubroId,
    selectedMarcaId, currentPage, perPage, orderBy, empresa, mapProducto]);

  useEffect(() => { fetchCombos(); }, [fetchCombos]);

  const fetchAllProductsForPdf = async () => {
    try {
      const cliente = JSON.parse(localStorage.getItem('clienteSelected'));
      const base = {
        searchTerm, selectedRamo: selectedRamoId, selectedRubro: selectedRubroId,
        selectedSubrubro: selectedSubrubroId, selectedMarca: selectedMarcaId, user_id: cliente?.id
      };

      const preview = await getCombosPaginate({ ...base, page: 1, perPage: 1 });
      const total = preview?.pagination?.totalItems || 0;
      if (total === 0) return [];

      const full = await getCombosPaginate({ ...base, page: 1, perPage: total });
      return full?.combos?.map(mapProducto) ?? [];
    } catch (e) { console.error(e); return []; }
  };

  const handleOpenPdf = async (format) => {
    setGenerandoPDF(true);
    try {
      const allProducts = await fetchAllProductsForPdf();
      if (!allProducts.length) return;
      const blob = await pdf(<ExportPdf products={allProducts} empresa={empresa} seccion="Combos"  format={format}/>).toBlob();
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (e) { console.error(e); }
    finally { setGenerandoPDF(false); }
  };

  if (loading) return <Loading message="Cargando Combos..." />;

  return (
    <Grid container spacing={2} p={2} alignItems="center">
      <CategoryFilter
        ramoId={selectedRamoId}
        setRamoId={setRamoId}       // ← prop name coincide con lo que usa el componente
        rubroId={selectedRubroId}
        setRubroId={setRubroId}
        subrubroId={selectedSubrubroId}
        setSubrubroId={setSubrubroId}
        marcaId={selectedMarcaId}
        setMarcaId={setMarcaId}
        clearAll={clearAll}
      />
      <Grid container spacing={2} sx={{ mb: 2 }} justifyContent="center">
        <ControlsRow
          isLogin={user?.id} products={products}
          generandoPDF={generandoPDF} setGenerandoPDF={setGenerandoPDF}
          handleOpenPdf={handleOpenPdf} viewMode={viewMode} setViewMode={setViewMode}
          orderBy={orderBy} setOrderBy={setOrderBy}
        />
      </Grid>
      <ProductList
        products={products} isCheckStock={empresa?.venta_sin_stock}
        viewMode={viewMode} totalPages={totalPages} currentPage={currentPage}
        perPage={perPage} totalItems={totalItems}
        handlePageChange={setCurrentPage} handlePerPageChange={setPerPage}
      />
    </Grid>
  );
};

export default CombosResults;